<template>
    <Modal
        ref="modalRef"
        :primary-button-disabled="isPrimaryButtonDisabled"
        :primary-button-label="primaryButtonLabel"
        :processing="form.processing"
        max-width="2xl"
        v-bind="$attrs"
        @submit="handleSubmit"
    >
        <template #title>
            <asset-short-info
                :asset="asset"
                :clickable="hasAnyPermission('asset.read')"
                :hide-curie-id="isPresenceBlocked"
                class="my-4"
                @asset-details-visited="modalRef?.close()"
            />
        </template>

        <task-meta :code="taskCode" :group="taskGroup" />

        <h2 class="mb-3 !text-xl leading-5 !font-bold">{{ title }}</h2>

        <div
            v-if="taskResolved"
            class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            data-testid="awaiting-approval-banner"
        >
            <template v-if="editable">{{ trans('tasks.modal.awaiting_approval.editor') }}</template>
            <template v-else>{{ trans('tasks.modal.awaiting_approval.readonly') }}</template>
        </div>

        <markdown v-if="description" :content="description" />

        <div v-if="isPresenceBlocked" class="mt-8 flex flex-col gap-4">
            <qr-code-scanner :key="scannerKey" :valid="isValidScan" @failure="onScanFailure" @reset="onScanReset" @success="onScannedCode" />

            <form class="flex flex-col gap-3" @submit.prevent="onManualSubmit">
                <jet-input
                    v-model="manualCurieId"
                    :horizontal="false"
                    :invalid-feedback="scanError"
                    :label="trans('assets.details.curie_id.label')"
                    :placeholder="trans('assets.details.curie_id.placeholder')"
                    :required="true"
                />
                <div class="flex justify-end">
                    <jet-button :disabled="!manualCurieId" type="submit" variant="primary">
                        {{ trans('general.action.go') }}
                    </jet-button>
                </div>
            </form>

            <div v-if="taskId && hasAnyPermission('asset.update_usage_category')" class="border-t pt-4">
                <jet-button :destructive="true" class="w-full" type="button" variant="secondary" @click="openAssetNotPresentConfirmation">
                    {{ trans('tasks.presence_detection.asset_not_present.button') }}
                </jet-button>
            </div>
        </div>

        <form
            :aria-disabled="isPresenceBlocked"
            :class="{ 'pointer-events-none opacity-50': isPresenceBlocked }"
            :inert="isPresenceBlocked"
            class="mt-8"
            @submit.prevent="handleSubmit"
        >
            <alert v-if="showPresenceDetectedHint" class="mb-4" color="success">
                {{ trans('tasks.presence_detection.detected.hint') }}
            </alert>
            <slot />

            <div v-if="performedByMode === 'auto'" class="mt-9 mb-3">
                <label class="form-label">{{ trans('assetlocationchecks.modal.performed_by.label') }}</label>
                <div
                    class="flex min-h-12 cursor-not-allowed items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-gray-600 opacity-75"
                >
                    {{ currentUserName }}
                </div>
            </div>
            <jet-select
                v-else
                v-model="form.performedById"
                :disabled="!editable"
                :enable-search="true"
                :horizontal="false"
                :invalid-feedback="form.errors.performedById"
                :label="trans('assetlocationchecks.modal.performed_by.label')"
                :options="assignableUsers"
                :required="true"
                formGroupClasses="mt-9 mb-3"
                name="performed_by_id"
            />

            <div v-if="performedByMode !== 'auto'" class="mb-4 flex w-100 justify-start">
                <small><em class="text-danger">*</em> {{ trans('general.required_fields') }}</small>
            </div>

            <div
                class="mt-9 flex cursor-pointer items-center gap-2 text-sm font-medium underline"
                data-testid="comment-collapse"
                @click.stop="toggleCommentAndUploadArea"
            >
                {{ trans('comments.action.add') }} <chevron-down :class="{ 'rotate-180': showComment }" />
            </div>
            <collapse :show="showComment || !!form.errors.description" @show="onShow">
                <row v-if="form.hasOwnProperty('description')" class="mt-3">
                    <column>
                        <jet-textarea
                            v-model="form.description"
                            :disabled="!editable"
                            :horizontal="false"
                            :invalidFeedback="form.errors.description"
                            :isValid="!form.errors.description"
                            :label="trans('general.comment.label')"
                            :rows="5"
                            name="description"
                            test-id="comment-textarea"
                        />
                    </column>
                </row>
                <row v-if="hasAnyPermission(['test.attachment.create'])" class="mt-3">
                    <column>
                        <div class="form-label">
                            {{ trans('test_record.form.fileupload.label') }}
                        </div>
                        <file-upload
                            ref="fileUploadRef"
                            v-model="form.attachments"
                            :disabled="!editable"
                            @upload-started="uploadInProgress = true"
                            @upload-completed="uploadInProgress = false"
                            @upload-failed="uploadInProgress = false"
                        />
                    </column>
                </row>
            </collapse>
        </form>
    </Modal>

    <confirm-modal
        v-if="taskId && hasAnyPermission('asset.update_usage_category')"
        :confirm-text="trans('tasks.presence_detection.asset_not_present.confirm.confirm_text')"
        :title="trans('tasks.presence_detection.asset_not_present.confirm.title')"
        name="confirm-asset-not-present"
        @confirm="markAssetNotPresent"
    >
        <p>{{ trans('tasks.presence_detection.asset_not_present.confirm.body_plain', { curieId: curieId }) }}</p>
    </confirm-modal>
</template>
<script setup>
import { computed, ref } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { visitModal } from '@inertiaui/modal-vue';
import { trans } from '@/mixins/i18n';
import { hasFeature } from '@/mixins/hasFeature.js';
import { useCurieIdScanner } from '@/composables/useCurieIdScanner.ts';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import Markdown from '@/Jetstream/Markdown.vue';
import TaskMeta from '@/Components/TaskMeta.vue';
import JetSelect from '@/Jetstream/Select.vue';
import JetTextarea from '@/Jetstream/Textarea.vue';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import Alert from '@/Components/Alert.vue';
import ConfirmModal from '@/Components/ConfirmModal.vue';
import QrCodeScanner from '@/Components/QrCodeScanner.vue';
import ChevronDown from '../../../icons/ChevronDown.vue';
import Collapse from '@/Components/Collapse.vue';
import FileUpload from '@/Components/FileUpload.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';

const modalRef = ref(null);
const fileUploadRef = ref(null);
const uploadInProgress = ref(false);

const props = defineProps({
    asset: { type: Object, required: true },
    title: { type: String, required: true },
    description: { type: String },
    taskCode: { type: String, required: true },
    taskGroup: { type: String },
    form: { type: Object, required: true },
    editable: { type: Boolean, required: true },
    assignableUsers: { type: Object, required: true },
    attachments: { type: Object },
    performedByMode: { type: String, default: 'select' },
    primaryButtonBlocked: { type: Boolean, default: false },
    taskResolved: { type: Boolean, default: false },
    primaryButtonLabel: {
        type: String,
        default: () => trans('general.action.submit'),
    },
    requiresPresenceDetection: { type: Boolean, default: false },
    presenceFromStartHereScan: { type: Boolean, default: false },
    curieId: { type: String, default: null },
    taskId: { type: String, default: null },
});

const { recordScan } = useCurieIdScanner();

const showComment = ref(true);

// Pre-seeded when the modal was opened by a fresh start-here scan that the server has
// already verified against the session — that scan IS the proof of presence, so the
// operator shouldn't have to scan again inside the modal.
const presenceLocallyDetected = ref(props.presenceFromStartHereScan);
const scannerKey = ref(0);
const manualCurieId = ref('');
const isValidScan = ref(undefined);
const scanError = ref(null);

const isPresenceBlocked = computed(() => {
    if (!hasFeature('task_presence_detection') || !props.requiresPresenceDetection || !props.curieId) {
        return false;
    }

    // Always require an in-modal scan when presence detection is required — neither a
    // prior session-stored scan nor an existing completed execution counts as proof,
    // since presence must be re-established at the moment the task is acted on.
    return !presenceLocallyDetected.value;
});

const showPresenceDetectedHint = computed(
    () => hasFeature('task_presence_detection') && props.requiresPresenceDetection && presenceLocallyDetected.value,
);

const handleCode = async (code, method) => {
    if (!code) return;

    if (code !== props.curieId) {
        isValidScan.value = false;
        scanError.value = trans('tasks.presence_detection.wrong_asset');
        setTimeout(() => {
            scannerKey.value += 1;
            isValidScan.value = undefined;
        }, 1200);
        return;
    }

    try {
        await recordScan(code, method);
        isValidScan.value = true;
        scanError.value = null;
        presenceLocallyDetected.value = true;
    } catch (error) {
        console.error('Failed to record Curie ID scan', error);
        isValidScan.value = false;
        scanError.value = trans('general.error.generic');
        presenceLocallyDetected.value = false;
    }
};

const onScannedCode = (code) => handleCode(code, 'qr');
const onManualSubmit = () => handleCode(manualCurieId.value.trim(), 'manual');
const onScanFailure = () => {
    isValidScan.value = false;
};
const onScanReset = () => {
    isValidScan.value = undefined;
};

const emit = defineEmits(['submit']);
const assignableUsers = computed(() =>
    (props.assignableUsers?.data || []).map((user) => ({
        value: user.id,
        label: user.name,
    })),
);

const currentUserName = computed(() => usePage().props.auth?.user?.name ?? '-');

const isPrimaryButtonDisabled = computed(() => {
    return !props.editable || !props.form.isDirty || uploadInProgress.value || props.primaryButtonBlocked || isPresenceBlocked.value;
});

const toggleCommentAndUploadArea = () => {
    showComment.value = !showComment.value;
};

const onShow = () => {
    setTimeout(() => {
        fileUploadRef.value?.$el.scrollIntoView && fileUploadRef.value?.$el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
};

const handleSubmit = () => {
    emit('submit', modalRef.value);
};

const openAssetNotPresentConfirmation = () => {
    visitModal('#confirm-asset-not-present');
};

const markAssetNotPresent = () => {
    if (!props.taskId) return;

    router.post(
        route('tasks.mark-asset-not-present', { task: props.taskId }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => modalRef.value?.close(),
        },
    );
};
</script>
